/**
 * Avatar — círculo de identidad con foto y fallback a iniciales.
 *
 * Diseño: renderiza el círculo usando la `className` que ya trae cada sitio de
 * uso (p.ej. `styles.patientAvatar`), de modo que conserva tamaño/estilo
 * existentes. Dentro muestra:
 *   - la foto (`fotoURL`) llenando el círculo (object-fit: cover), o
 *   - `fallback` (p.ej. un ícono), o
 *   - las iniciales de `nombre`.
 * Si la imagen falla al cargar, cae automáticamente al fallback/iniciales.
 *
 * `zoomable`: si hay foto, al hacer clic se abre un visor a pantalla completa
 * (lightbox) con la imagen ampliada. El clic no propaga al contenedor (evita
 * disparar la navegación de una fila/botón que envuelva al avatar).
 *
 * Uso mínimo (solo donde se necesita mostrar foto):
 *   <Avatar className={styles.patientAvatar} nombre={n} fotoURL={url} zoomable />
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { CSSProperties, ReactNode, MouseEvent } from 'react';
import { getInitials } from '../../utils/initials';

interface AvatarProps {
  nombre?: string | null;
  fotoURL?: string | null;
  /** className del círculo (tamaño/fondo/estilo del sitio de uso). */
  className?: string;
  /** Fallback custom cuando no hay foto (p.ej. un ícono). Default = iniciales. */
  fallback?: ReactNode;
  style?: CSSProperties;
  title?: string;
  /** Si hay foto, permite ampliarla en un visor a pantalla completa al hacer clic. */
  zoomable?: boolean;
}

const imgStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%',
  display: 'block',
};

export function Avatar({ nombre, fotoURL, className, fallback, style, title, zoomable }: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const [zoom, setZoom] = useState(false);

  // Reset del estado de error cuando cambia la URL (p.ej. tras subir nueva foto).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setErrored(false);
  }, [fotoURL]);

  const mostrarFoto = !!fotoURL && !errored;
  const puedeAmpliar = zoomable && mostrarFoto;

  const abrirZoom = (e: MouseEvent) => {
    if (!puedeAmpliar) return;
    e.stopPropagation();
    e.preventDefault();
    setZoom(true);
  };

  return (
    <>
      <div className={className} style={style} title={title ?? nombre ?? undefined}>
        {mostrarFoto ? (
          <img
            src={fotoURL as string}
            alt={nombre ? `Foto de ${nombre}` : 'Foto de perfil'}
            style={puedeAmpliar ? { ...imgStyle, cursor: 'zoom-in' } : imgStyle}
            onError={() => setErrored(true)}
            onClick={puedeAmpliar ? abrirZoom : undefined}
            role={puedeAmpliar ? 'button' : undefined}
            aria-label={puedeAmpliar ? `Ampliar foto de ${nombre ?? 'perfil'}` : undefined}
            loading="lazy"
          />
        ) : (
          fallback ?? getInitials(nombre)
        )}
      </div>

      {zoom && puedeAmpliar && (
        <AvatarLightbox
          src={fotoURL as string}
          nombre={nombre}
          onClose={() => setZoom(false)}
        />
      )}
    </>
  );
}

/* ── Visor a pantalla completa (lightbox) ── */
function AvatarLightbox({ src, nombre, onClose }: {
  src: string;
  nombre?: string | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Bloquear scroll del fondo mientras el visor está abierto.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 100000,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    cursor: 'zoom-out',
    animation: 'avatarZoomFade 140ms ease-out',
  };

  return createPortal(
    <div
      style={overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={nombre ? `Foto ampliada de ${nombre}` : 'Foto ampliada'}
    >
      <style>{`@keyframes avatarZoomFade{from{opacity:0}to{opacity:1}}`}</style>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Cerrar"
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(255,255,255,0.14)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <X size={22} aria-hidden="true" />
      </button>
      <img
        src={src}
        alt={nombre ? `Foto de ${nombre}` : 'Foto de perfil'}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          objectFit: 'contain',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          cursor: 'default',
        }}
      />
      {nombre && (
        <span
          style={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            background: 'rgba(0,0,0,0.4)',
            padding: '6px 14px',
            borderRadius: 999,
          }}
        >
          {nombre}
        </span>
      )}
    </div>,
    document.body,
  );
}

export default Avatar;
